import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as api from "../api";
import { jornadaPorDefecto, normalizarJornada } from "../jornada";

// La jornada de cada persona que se muestra, pedida de una vez y guardada.
//
// `jornadaDe(userId)` siempre devuelve una jornada utilizable: la de Odoo si la hay y, si la
// API falla o no trae a esa persona, 8 h de lunes a viernes con origen 'defecto' y el motivo
// a la vista (`origen`, `motivo_defecto`) para que la pantalla diga que es un limite por defecto.
const cache = new Map();   // idALM -> jornada normalizada, para no repetir el viaje al volver
// El defecto, un objeto por motivo: los efectos de las pantallas dependen de la jornada, y uno
// nuevo en cada llamada los relanzaria sin parar.
const porDefecto = new Map();
const defectoDe = motivo => {
  if (!porDefecto.has(motivo)) porDefecto.set(motivo, jornadaPorDefecto(motivo));
  return porDefecto.get(motivo);
};

const REINTENTO_MS = 30000;

// `quien`: el usuario que mira. La jornada se lee con SU sesion de Odoo y dentro de SU ambito, asi
// que lo guardado de otro usuario (o de una sesion sin Odoo) no vale: al cambiar se vacia la cache.
export function useJornada(userIds, quien = null) {
  const ids = useMemo(() => [...new Set((userIds || []).filter(Boolean).map(String))]
    .filter(s => /^\d+$/.test(s)).sort((a, b) => a - b), [(userIds || []).join(",")]); // eslint-disable-line
  const clave = ids.join(",");
  const [version, redibujar] = useState(0);
  const [falloApi, setFalloApi] = useState(false);
  const [intento, setIntento] = useState(0);
  const anterior = useRef(quien);
  if (anterior.current !== quien) { anterior.current = quien; cache.clear(); }

  useEffect(() => {
    const faltan = ids.filter(id => !cache.has(id));
    if (!faltan.length) return;
    let vivo = true, reintento = null;
    api.getJornada(faltan)
      .then(r => {
        for (const id of faltan) cache.set(id, normalizarJornada(r?.jornadas?.[id]));
        if (vivo) { setFalloApi(false); redibujar(n => n + 1); }
      })
      .catch(() => {
        if (!vivo) return;
        setFalloApi(true);
        // Un fallo suelto no deja la pantalla con el limite por defecto para siempre
        reintento = setTimeout(() => setIntento(n => n + 1), REINTENTO_MS);
      });
    return () => { vivo = false; clearTimeout(reintento); };
  }, [clave, quien, intento]); // eslint-disable-line

  const jornadaDe = useCallback(userId => {
    const j = cache.get(String(userId));
    if (j) return j;
    return defectoDe(falloApi ? "no se pudo leer la jornada" : null);
  }, [falloApi, version]);

  return { jornadaDe };
}

// Para las pruebas: cada una parte de una cache limpia.
export const vaciarCacheJornada = () => cache.clear();
